import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Radio,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import { useMemo, useState } from 'react';

import type { RoofTopI } from 'declarations/dto/roofTops';

type InsightsDealerSelectPropsT = {
  roofTops: RoofTopI[];
  /** Full list for name lookup when selected dealer is not in filtered list */
  roofTopsList: RoofTopI[] | null;
  value: string | null;
  onChange: (id: string | null) => void;
};

/**
 * Dealer dropdown for Insights tab – single-select, searchable.
 * Filtered by OEM and Dealer Group. Selection syncs with DealerCard (RoofTopId).
 * Shows selected dealer name in the button.
 */
export const InsightsDealerSelect: FC<InsightsDealerSelectPropsT> = ({
  roofTops,
  roofTopsList,
  value,
  onChange,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRoofTops = useMemo((): RoofTopI[] => {
    if (!searchQuery.trim()) return roofTops;
    const q = searchQuery.trim().toLowerCase();
    return roofTops.filter((r): boolean => (r.name ?? '').toLowerCase().includes(q));
  }, [roofTops, searchQuery]);

  const selectedDealer = useMemo((): RoofTopI | null => {
    if (!value) return null;
    const inFiltered = roofTops.find((r): boolean => r.id === value);
    if (inFiltered) return inFiltered;
    return (roofTopsList ?? []).find((r): boolean => r.id === value) ?? null;
  }, [roofTops, roofTopsList, value]);

  const handleMenuOpen = (e: MouseEvent<HTMLButtonElement>): void => {
    setSearchQuery('');
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = (): void => {
    setMenuAnchor(null);
    setSearchQuery('');
  };

  const handleSelectDealer = (roofId: string | null): void => {
    onChange(roofId);
    setMenuAnchor(null);
  };

  if (roofTops.length === 0) return null;

  return (
    <>
      <Tooltip
        title="Select dealer for report (syncs with Dealer Card)"
        arrow
        placement="top"
        slotProps={{
          popper: {
            modifiers: [{ name: 'flip', enabled: true }],
            sx: { zIndex: 1300 },
          },
        }}
      >
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              minWidth: 160,
              justifyContent: 'space-between',
              fontSize: 12,
              height: 28,
              textTransform: 'none',
            }}
            endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
            onClick={handleMenuOpen}
          >
            {selectedDealer ? selectedDealer.name ?? 'Dealer' : 'Select dealer'}
          </Button>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            maxHeight: 380,
            width: 280,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 0,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search dealers"
            value={searchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => setSearchQuery(e.target.value)}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear dealer search"
                    size="small"
                    onClick={(): void => setSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <ListItemButton onClick={(): void => handleSelectDealer(null)}>
            <ListItemIcon>
              <Radio checked={!value} edge="start" disableRipple />
            </ListItemIcon>
            <ListItemText primary="All" />
          </ListItemButton>
          {filteredRoofTops.map(
            (roof): JSX.Element => (
              <ListItemButton key={roof.id} onClick={(): void => handleSelectDealer(roof.id)}>
                <ListItemIcon>
                  <Radio checked={value === roof.id} edge="start" disableRipple />
                </ListItemIcon>
                <ListItemText primary={roof.name} />
              </ListItemButton>
            )
          )}
          {searchQuery.trim() && filteredRoofTops.length === 0 && (
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                px: 2,
                py: 1,
              }}
            >
              No dealers match your search.
            </Typography>
          )}
        </List>
      </Menu>
    </>
  );
};
